import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from './LocationContext';
import { getCartItemKey } from '../utils/cartProductHelpers';

const CartContext = createContext();

export const CART_TAX_RATE = 0.2;
export const CART_FREE_SHIPPING_THRESHOLD = Infinity; // Livraison toujours 50 DH
export const CART_SHIPPING_FEE = 50;

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const { isCasaRegion } = useLocation();
    
    const [cartItems, setCartItems] = useState(() => {
        // Load cart from localStorage on initial render
        const savedCart = localStorage.getItem('cart');
        return savedCart ? JSON.parse(savedCart) : [];
    });

    // Save cart to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('cart', JSON.stringify(cartItems));
    }, [cartItems]);

    const addToCart = (product, quantity = 1) => {
        const key = getCartItemKey(product);

        setCartItems(prevItems => {
            const existingItem = prevItems.find(item => getCartItemKey(item) === key);
            
            if (existingItem) {
                return prevItems.map(item =>
                    getCartItemKey(item) === key
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                );
            }

            return [...prevItems, { ...product, cartKey: key, quantity }];
        });
    };

    const removeFromCart = (productIdOrKey) => {
        const key = String(productIdOrKey);
        setCartItems(prevItems =>
            prevItems.filter(item => getCartItemKey(item) !== key)
        );
    };

    const updateQuantity = (productIdOrKey, quantity) => {
        const key = String(productIdOrKey);
        if (quantity <= 0) {
            removeFromCart(key);
            return;
        }
        
        setCartItems(prevItems =>
            prevItems.map(item =>
                getCartItemKey(item) === key
                    ? { ...item, quantity }
                    : item
            )
        );
    };

    const incrementQuantity = (productIdOrKey) => {
        const key = String(productIdOrKey);
        setCartItems(prevItems =>
            prevItems.map(item =>
                getCartItemKey(item) === key
                    ? { ...item, quantity: item.quantity + 1 }
                    : item
            )
        );
    };

    const decrementQuantity = (productIdOrKey) => {
        const key = String(productIdOrKey);
        setCartItems(prevItems =>
            prevItems.map(item => {
                if (getCartItemKey(item) === key) {
                    const newQuantity = item.quantity - 1;
                    return newQuantity > 0 ? { ...item, quantity: newQuantity } : item;
                }
                return item;
            }).filter(item => item.quantity > 0)
        );
    };

    const clearCart = () => {
        setCartItems([]);
    };

    // Get price based on location
    const getItemPrice = (item) => {
        if (isCasaRegion && item.price_casa) {
            return parseFloat(item.price_casa);
        }
        return parseFloat(item.price) || 0;
    };

    const getCartTotal = useCallback(() => {
        return cartItems.reduce((total, item) => {
            const price = getItemPrice(item);
            return total + (price * item.quantity);
        }, 0);
    }, [cartItems, isCasaRegion]);

    const getCartTax = useCallback(() => {
        return getCartTotal() * CART_TAX_RATE;
    }, [getCartTotal]);

    const getCartShipping = useCallback(() => {
        return CART_SHIPPING_FEE;
    }, []);

    const getCartGrandTotal = useCallback(() => {
        return getCartTotal() * (1 + CART_TAX_RATE) + getCartShipping();
    }, [getCartTotal, getCartShipping]);

    const getCartItemsCount = () => {
        return cartItems.reduce((count, item) => count + item.quantity, 0);
    };

    const isInCart = (productId) => {
        return cartItems.some(item => String(item.id) === String(productId));
    };

    const value = {
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        incrementQuantity,
        decrementQuantity,
        clearCart,
        getCartTotal,
        getCartTax,
        getCartShipping,
        getCartGrandTotal,
        getCartItemsCount,
        isInCart,
        getItemPrice,
        isCasaRegion,
        getCartItemKey,
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};

export default CartContext;
